/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { formatIsoDate, parseIsoDate } from '@shared/vueltoDateTime';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import type { TicketListRowGroup } from '../../shared/ticket/TicketListRow'
import { TicketButton } from '../../shared/ticket/TicketButton'
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField'
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import {
  listMyCommissions,
  listMyCommissionSummary,
  listMyAdjustments,
} from '../infra/commissionsRepo';
import type {
  CommissionEarning,
  MyCommissionSummary,
  CommissionAdjustment,
  EarningStatus,
} from '../domain/commissionTypes';
import {
  EARNING_STATUS_LABELS,
  ADJUSTMENT_TYPE_LABELS,
  commissionEarningTitle,
  dedupeKpiValues,
  isNoPlanEarning,
} from '../domain/commissionTypes';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { SaleDetailModal } from '../../sales/components/SaleDetailModal';
import { formatMoney } from '../../shared/format/public-api';

type DatePreset = 'this_month' | 'last_month' | 'all' | 'custom';

const PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'this_month', label: 'Este mes' },
  { key: 'last_month', label: 'Mes anterior' },
  { key: 'all', label: 'Todo' },
];

const EARNING_STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'muted'> = {
  pending: 'warn',
  settled: 'ok',
  voided: 'bad',
};


function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  } catch {
    return iso;
  }
}

function presetToRange(preset: DatePreset): { from: Date | null; to: Date | null } {
  const now = new Date();
  if (preset === 'this_month') {
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1),
      to: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
    };
  }
  if (preset === 'last_month') {
    return {
      from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      to: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
    };
  }
  return { from: null, to: null };
}

function presetLabel(preset: DatePreset): string {
  if (preset === 'this_month') return 'Este mes';
  if (preset === 'last_month') return 'Mes anterior';
  if (preset === 'custom') return 'Personalizado';
  return 'Todo el histórico';
}

export function MyCommissionsScreen() {
  const { layoutWide } = useContentLayout('dataWide');
  const capabilityGate = useCapabilityGate('commissions', {
    title: 'Comisiones no disponibles',
    subtitle: 'Este rubro no tiene comisiones.',
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [preset, setPreset] = useState<DatePreset>('this_month');
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = useState<EarningStatus | 'all'>('all');
  const branchId = useSessionStore((s) => s.currentBranchId);
  const [detailSaleId, setDetailSaleId] = useState<string | null>(null);

  const [summary, setSummary] = useState<MyCommissionSummary | null>(null);
  const [earnings, setEarnings] = useState<CommissionEarning[]>([]);
  const [adjustments, setAdjustments] = useState<CommissionAdjustment[]>([]);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const range = preset === 'custom' ? { from: customFrom, to: customTo } : presetToRange(preset);
      const fromIso = range.from ? range.from.toISOString() : null;
      const toIso = range.to ? range.to.toISOString() : null;
      const [sum, earns, adjs] = await Promise.all([
        listMyCommissionSummary({ fromIso, toIso }),
        listMyCommissions({ fromIso, toIso, status: statusFilter === 'all' ? null : statusFilter }),
        listMyAdjustments({ fromIso, toIso }),
      ]);
      setSummary(sum);
      setEarnings(earns);
      setAdjustments(adjs);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar tus comisiones.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [preset, customFrom, customTo, statusFilter]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  if (!capabilityGate.allowed) {
    return <OperationalScrollScreen header={null} body={capabilityGate.blocked} />;
  }

  if (loading) {
    return (
      <OperationalScrollScreen
        header={null}
        body={
          <ModuleEmptyState
            variant="nest"
            kicker="Mis comisiones"
            loading
            loadingText="Cargando tus comisiones…"
          />
        }
      />
    );
  }

  if (error) {
    return (
      <OperationalScrollScreen
        header={null}
        body={
          <ModuleEmptyState
            variant="nest"
            kicker="Mis comisiones"
            title="No se pudieron cargar tus comisiones"
            subtitle={error}
            primaryAction={{ label: 'Reintentar', onPress: () => void load() }}
          />
        }
      />
    );
  }

  // #1454: KPIs con el mismo número se colapsan (queda el más accionable);
  // «Ajustes» solo aparece cuando hay ajustes reales.
  const kpiItems: TicketKpiItem[] = summary
    ? dedupeKpiValues([
        { label: 'Bruto', value: formatMoney(summary.grossEarnings) },
        { label: 'Pendiente', value: formatMoney(summary.pendingEarnings), tone: summary.pendingEarnings > 0 ? 'warn' as const : 'default' as const },
        { label: 'Liquidado', value: formatMoney(summary.settledEarnings), tone: 'ok' as const },
        ...(summary.adjustmentsTotal !== 0
          ? [{ label: 'Ajustes', value: formatMoney(summary.adjustmentsTotal) }]
          : []),
        { label: 'Neto a cobrar', value: formatMoney(summary.netPayable), tone: 'ok' as const },
      ])
    : [];

  const header = (
    <TicketModuleHeader
      kicker="MIS COMISIONES"
      title="Mis comisiones"
      meta={presetLabel(preset)}
    >
      {/* Date presets */}
      <View style={styles.presetRow}>
        {PRESETS.map((p) => (
          <TouchableOpacity
            key={p.key}
            style={[styles.presetBtn, preset === p.key && styles.presetBtnActive]}
            onPress={() => { setPreset(p.key); setCustomFrom(null); setCustomTo(null); }}
          >
            <Text style={[styles.presetText, preset === p.key && styles.presetTextActive]}>{p.label}</Text>
          </TouchableOpacity>
        ))}
        <TouchableOpacity
          style={[styles.presetBtn, preset === 'custom' && styles.presetBtnActive]}
          onPress={() => setPreset('custom')}
        >
          <Text style={[styles.presetText, preset === 'custom' && styles.presetTextActive]}>Personalizado</Text>
        </TouchableOpacity>
      </View>

      {/* Status filter */}
      <View style={styles.presetRow}>
        <TouchableOpacity
          style={[styles.presetBtn, statusFilter === 'all' && styles.presetBtnActive]}
          onPress={() => setStatusFilter('all')}
        >
          <Text style={[styles.presetText, statusFilter === 'all' && styles.presetTextActive]}>Todos</Text>
        </TouchableOpacity>
        {(['pending', 'settled', 'voided'] as EarningStatus[]).map((s) => (
          <TouchableOpacity
            key={s}
            style={[styles.presetBtn, statusFilter === s && styles.presetBtnActive]}
            onPress={() => setStatusFilter(s)}
          >
            <Text style={[styles.presetText, statusFilter === s && styles.presetTextActive]}>
              {EARNING_STATUS_LABELS[s] ?? s}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      {preset === 'custom' ? (
        <View style={styles.customDateRow}>
          <View style={styles.customDateField}>
            <Text style={styles.customDateLabel}>Desde</Text>
            <InventoryIsoDateField
              value={customFrom ? formatIsoDate(customFrom) : ''}
              onChange={(ymd) => {
                if (!ymd) { setCustomFrom(null); return; }
                const d = parseIsoDate(ymd); d.setHours(0, 0, 0, 0); setCustomFrom(d);
              }}
              emptyLabel="Elegí fecha"
              ariaLabel="Fecha desde"
              showClearButton={false}
              iosEmptyBehavior="openPicker"
            />
          </View>
          <View style={styles.customDateField}>
            <Text style={styles.customDateLabel}>Hasta</Text>
            <InventoryIsoDateField
              value={customTo ? formatIsoDate(customTo) : ''}
              onChange={(ymd) => {
                if (!ymd) { setCustomTo(null); return; }
                const d = parseIsoDate(ymd); d.setHours(23, 59, 59, 999); setCustomTo(d);
              }}
              emptyLabel="Elegí fecha"
              ariaLabel="Fecha hasta"
              showClearButton={false}
              iosEmptyBehavior="openPicker"
            />
          </View>
          <TicketButton label="Aplicar" variant="gold" onPress={() => void load()} style={styles.applyBtn} />
        </View>
      ) : null}
    </TicketModuleHeader>
  );

  const body = (
    <>
      {/* Summary KPIs */}
      {summary ? (
        <PaperSheet>
          <TicketKpiStrip items={kpiItems} variant="soft" layout={layoutWide ? 'desk' : 'mobile'} />
        </PaperSheet>
      ) : null}

      {/* Top-level empty state */}
      {!summary && earnings.length === 0 && adjustments.length === 0 ? (
        <ModuleEmptyState
          variant="nest"
          kicker="Mis comisiones"
          title="Sin comisiones"
          subtitle="No tenés comisiones ni ajustes para el período seleccionado."
        />
      ) : null}

      {/* Earnings detail */}
      {earnings.length > 0 || summary ? (
        <>
          <Text style={styles.sectionTitle}>Detalle de comisiones ({earnings.length})</Text>
          {earnings.length === 0 ? (
            <ModuleEmptyState
              variant="nest"
              kicker="Mis comisiones"
              title="Sin comisiones en este período"
              subtitle="No tenés comisiones generadas para el período seleccionado."
            />
          ) : (
            <PaperSheet>
              {earnings.some(isNoPlanEarning) ? (
                <View style={styles.noPlanHint}>
                  <Text style={styles.noPlanHintText}>
                    Tu usuario no tiene plan de comisión asignado — esas ventas
                    no suman. Avisale al dueño.
                  </Text>
                </View>
              ) : null}
              {earnings.map((e, idx) => {
                const group: TicketListRowGroup = idx === earnings.length - 1 ? 'groupedLast' : 'grouped';
                const hasSaleLink = e.sourceType === 'sale_item' && e.sourceRef;
                const noPlan = isNoPlanEarning(e);
                return (
                  <TicketListRow
                    key={e.id}
                    title={noPlan ? 'Sin comisión generada' : commissionEarningTitle(e.description)}
                    subtitle={formatDate(e.earnedAt)}
                    meta={`Base: ${formatMoney(e.commissionableAmount)}`}
                    value={formatMoney(e.commissionAmount)}
                    statusLabel={noPlan ? 'Sin plan' : EARNING_STATUS_LABELS[e.status]}
                    statusTone={noPlan ? 'muted' : EARNING_STATUS_TONE[e.status] ?? 'muted'}
                    chipLabel={e.ratePercent != null ? `${e.ratePercent}%` : undefined}
                    chipTone="default"
                    group={group}
                    onPress={hasSaleLink ? () => setDetailSaleId(e.sourceRef!) : undefined}
                    showChevron={Boolean(hasSaleLink)}
                    accessibilityLabel={hasSaleLink ? 'Ver venta' : undefined}
                  />
                );
              })}
            </PaperSheet>
          )}
        </>
      ) : null}

      {/* Adjustments */}
      {adjustments.length > 0 ? (
        <>
          <Text style={styles.sectionTitle}>Adelantos y ajustes ({adjustments.length})</Text>
          <PaperSheet>
            {adjustments.map((a, idx) => {
              const group: TicketListRowGroup = idx === adjustments.length - 1 ? 'groupedLast' : 'grouped';
              return (
                <TicketListRow
                  key={a.id}
                  title={ADJUSTMENT_TYPE_LABELS[a.adjustmentType]}
                  subtitle={a.reason}
                  meta={formatDate(a.effectiveDate)}
                  value={`${a.amount >= 0 ? '+' : ''}${formatMoney(a.amount)}`}
                  statusLabel={a.status === 'pending' ? 'Pendiente' : a.status === 'settled' ? 'Liquidado' : 'Anulado'}
                  statusTone={a.status === 'pending' ? 'warn' : a.status === 'settled' ? 'ok' : 'bad'}
                  group={group}
                />
              );
            })}
          </PaperSheet>
        </>
      ) : null}
    </>
  );

  return (
    <OperationalScrollScreen
      header={header}
      body={body}
      refreshing={refreshing}
      onRefresh={() => void load(true)}
      modals={
        <SaleDetailModal
          visible={detailSaleId != null}
          saleId={detailSaleId}
          branchId={branchId ?? null}
          onClose={() => setDetailSaleId(null)}
        />
      }
    />
  );
}

const styles = StyleSheet.create({
  presetRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm },
  presetBtn: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  presetBtnActive: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorderSoft,
  },
  presetText: { fontSize: 13, fontWeight: '500', color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  presetTextActive: { color: ticketColors.brandInk, fontWeight: '700' },
  customDateRow: { flexDirection: 'row', alignItems: 'flex-end', gap: spacing.sm, marginTop: spacing.sm, flexWrap: 'wrap' },
  customDateField: { flex: 1, minWidth: 120 },
  customDateLabel: { fontSize: 12, fontWeight: '600', color: ticketColors.ink2, marginBottom: spacing.xs, fontFamily: ticketFonts.sans },
  applyBtn: { paddingHorizontal: spacing.lg, paddingVertical: 10 },
  sectionTitle: { fontSize: 15, fontWeight: '700', color: ticketColors.ink, marginTop: spacing.lg, marginBottom: spacing.sm, fontFamily: ticketFonts.sans },
  noPlanHint: {
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    backgroundColor: ticketColors.paper2,
  },
  noPlanHintText: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
});
